import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import RiderLayout from "@/components/RiderLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  MapPin, Clock, Camera, AlertTriangle, CheckCircle, 
  LogIn, LogOut, Package, Wallet, TrendingUp, Navigation
} from "lucide-react";

// Location tracking interval in milliseconds (30 seconds)
const LOCATION_TRACKING_INTERVAL = 30000;

const RiderDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const locationIntervalRef = useRef(null);
  const locationBufferRef = useRef([]);
  
  const [permissionsGranted, setPermissionsGranted] = useState({ camera: false, location: false });
  const [permissionsChecked, setPermissionsChecked] = useState(false);
  const [attendance, setAttendance] = useState(null);
  const [loadingAttendance, setLoadingAttendance] = useState(true);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [trackingActive, setTrackingActive] = useState(false);
  const [lastLocationUpdate, setLastLocationUpdate] = useState(null);

  // Check permissions on mount
  useEffect(() => {
    checkPermissions();
  }, []);

  // Fetch today's attendance
  useEffect(() => {
    fetchTodayAttendance();
  }, []);

  // Start location tracking when checked in
  useEffect(() => {
    if (attendance?.is_checked_in && permissionsGranted.location) {
      startLocationTracking();
    } else {
      stopLocationTracking();
    }
    
    return () => stopLocationTracking();
  }, [attendance?.is_checked_in, permissionsGranted.location]);

  const checkPermissions = async () => {
    try {
      let cameraGranted = false;
      try {
        const cameraResult = await navigator.permissions.query({ name: 'camera' });
        cameraGranted = cameraResult.state === 'granted';
      } catch (e) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ video: true });
          stream.getTracks().forEach(track => track.stop());
          cameraGranted = true;
        } catch (err) {
          cameraGranted = false;
        }
      }

      let locationGranted = false;
      try {
        const locationResult = await navigator.permissions.query({ name: 'geolocation' });
        locationGranted = locationResult.state === 'granted';
      } catch (e) {
        locationGranted = false;
      }

      setPermissionsGranted({ camera: cameraGranted, location: locationGranted });
      setPermissionsChecked(true);

      if (locationGranted) {
        getCurrentLocation();
      }
    } catch (error) {
      console.error("Permission check error:", error);
      setPermissionsChecked(true);
    }
  };

  const requestPermissions = async () => {
    try {
      const cameraStream = await navigator.mediaDevices.getUserMedia({ video: true });
      cameraStream.getTracks().forEach(track => track.stop());
      
      await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000
        });
      });

      setPermissionsGranted({ camera: true, location: true });
      toast.success("Permissions granted!");
      getCurrentLocation();
      
      // Record login activity
      recordLoginActivity();
    } catch (error) {
      console.error("Permission request error:", error);
      toast.error("Please enable camera and location access in your browser settings");
    }
  };

  const recordLoginActivity = async () => {
    try {
      // Get location for login activity
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          await axios.post(`${API}/rider/activity`, {
            activity_type: "LOGIN",
            location: {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
              accuracy: position.coords.accuracy
            }
          });
        },
        () => {
          // Login without location if unavailable
          axios.post(`${API}/rider/activity`, {
            activity_type: "LOGIN"
          }).catch(console.error);
        }
      );
    } catch (e) {
      console.error("Failed to record login:", e);
    }
  };

  const getCurrentLocation = () => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCurrentLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: new Date().toISOString()
        });
        setLocationError(null);
      },
      (error) => {
        setLocationError(error.message);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const startLocationTracking = useCallback(() => {
    if (locationIntervalRef.current) return; // Already tracking
    
    setTrackingActive(true);
    console.log("Starting location tracking every 30 seconds");
    
    // Initial update
    sendLocationUpdate();
    
    // Set up interval
    locationIntervalRef.current = setInterval(() => {
      sendLocationUpdate();
    }, LOCATION_TRACKING_INTERVAL);
  }, []);

  const stopLocationTracking = useCallback(() => {
    if (locationIntervalRef.current) {
      clearInterval(locationIntervalRef.current);
      locationIntervalRef.current = null;
    }
    setTrackingActive(false);
    
    // Send any buffered locations
    if (locationBufferRef.current.length > 0) {
      sendBufferedLocations();
    }
  }, []);

  const sendLocationUpdate = () => {
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const locationData = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          speed: position.coords.speed,
          heading: position.coords.heading,
        };
        
        try {
          // Try to get battery level
          if (navigator.getBattery) {
            const battery = await navigator.getBattery();
            locationData.battery_level = Math.round(battery.level * 100);
          }
        } catch (e) {
          // Battery API not available
        }

        try {
          await axios.post(`${API}/rider/location/update`, locationData);
          setLastLocationUpdate(new Date().toISOString());
          setCurrentLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: new Date().toISOString()
          });
        } catch (error) {
          console.error("Failed to send location update:", error);
          // Buffer the location for later sync
          locationBufferRef.current.push({
            ...locationData,
            timestamp: new Date().toISOString(),
            date: new Date().toISOString().split('T')[0]
          });
        }
      },
      (error) => {
        console.error("Geolocation error:", error);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const sendBufferedLocations = async () => {
    if (locationBufferRef.current.length === 0) return;
    
    const locations = [...locationBufferRef.current];
    locationBufferRef.current = [];
    
    try {
      await axios.post(`${API}/rider/location/batch`, locations);
      console.log(`Synced ${locations.length} buffered locations`);
    } catch (error) {
      console.error("Failed to sync buffered locations:", error);
      // Put them back in the buffer
      locationBufferRef.current = [...locations, ...locationBufferRef.current];
    }
  };

  const fetchTodayAttendance = async () => {
    setLoadingAttendance(true);
    try {
      const res = await axios.get(`${API}/rider/attendance/today`);
      setAttendance(res.data);
    } catch (e) {
      setAttendance(null);
    } finally {
      setLoadingAttendance(false);
    }
  };

  const formatTime = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
    } catch {
      return iso;
    }
  };

  const isCheckedIn = attendance?.is_checked_in;

  // Show permission request screen if not granted
  if (permissionsChecked && (!permissionsGranted.camera || !permissionsGranted.location)) {
    return (
      <RiderLayout user={user} onLogout={onLogout}>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
          <div className="w-20 h-20 rounded-full bg-orange-100 flex items-center justify-center mb-6">
            <AlertTriangle className="w-10 h-10 text-orange-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">Permissions Required</h2>
          <p className="text-slate-600 mb-6">
            To use the rider app, you need to grant camera and location access.
          </p>
          
          <div className="space-y-3 mb-6 w-full max-w-xs">
            <div className={`flex items-center justify-between p-3 rounded-lg ${
              permissionsGranted.camera ? "bg-emerald-50" : "bg-red-50"
            }`}>
              <div className="flex items-center gap-2">
                <Camera className={`w-5 h-5 ${permissionsGranted.camera ? "text-emerald-600" : "text-red-600"}`} />
                <span className="text-sm font-medium">Camera</span>
              </div>
              {permissionsGranted.camera ? (
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              ) : (
                <Badge variant="outline" className="text-red-600 border-red-300">Required</Badge>
              )}
            </div>
            <div className={`flex items-center justify-between p-3 rounded-lg ${
              permissionsGranted.location ? "bg-emerald-50" : "bg-red-50"
            }`}>
              <div className="flex items-center gap-2">
                <MapPin className={`w-5 h-5 ${permissionsGranted.location ? "text-emerald-600" : "text-red-600"}`} />
                <span className="text-sm font-medium">Location</span>
              </div>
              {permissionsGranted.location ? (
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              ) : (
                <Badge variant="outline" className="text-red-600 border-red-300">Required</Badge>
              )}
            </div>
          </div>

          <Button 
            onClick={requestPermissions}
            className="w-full max-w-xs bg-orange-500 hover:bg-orange-600"
          >
            Grant Permissions
          </Button>
          <p className="text-xs text-slate-400 mt-4">
            These permissions are required for attendance and live tracking.
          </p>
        </div>
      </RiderLayout>
    );
  }

  return (
    <RiderLayout user={user} onLogout={onLogout}>
      <div className="space-y-4" data-testid="rider-dashboard">
        {/* Greeting */}
        <div className="mb-2">
          <h1 className="text-2xl font-bold text-slate-800">
            {new Date().getHours() < 12 ? "Good Morning" : new Date().getHours() < 17 ? "Good Afternoon" : "Good Evening"}, {user?.name?.split(" ")[0] || "Rider"}!
          </h1>
          <p className="text-slate-500 text-sm">{new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}</p>
        </div>

        {/* Attendance Status Card */}
        <Card className={`border-0 shadow-md ${
          isCheckedIn ? "bg-emerald-50 border-l-4 border-l-emerald-500" : "bg-orange-50 border-l-4 border-l-orange-500"
        }`}>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 mb-1">Today's Status</p>
                {loadingAttendance ? (
                  <p className="text-slate-500">Loading...</p>
                ) : isCheckedIn ? (
                  <>
                    <Badge className="bg-emerald-100 text-emerald-700 mb-1">
                      <CheckCircle className="w-3.5 h-3.5 mr-1" /> On Duty
                    </Badge>
                    <p className="text-xs text-slate-500">Since {formatTime(attendance.check_in_time)}</p>
                  </>
                ) : (
                  <>
                    <Badge variant="outline" className="text-orange-600 border-orange-300 mb-1">
                      <Clock className="w-3.5 h-3.5 mr-1" /> Off Duty
                    </Badge>
                    <p className="text-xs text-slate-500">
                      {attendance?.check_out_time 
                        ? `Checked out at ${formatTime(attendance.check_out_time)}`
                        : "Please check in to start your shift"
                      }
                    </p>
                  </>
                )}
              </div>
              <Button 
                onClick={() => navigate("/rider/attendance")}
                className={isCheckedIn ? "bg-red-500 hover:bg-red-600" : "bg-emerald-500 hover:bg-emerald-600"}
                size="sm"
              >
                {isCheckedIn ? (
                  <><LogOut className="w-4 h-4 mr-1" /> Check Out</>
                ) : (
                  <><LogIn className="w-4 h-4 mr-1" /> Check In</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-3">
          <Card className="border-0 shadow-md">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Package className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Today's Trips</p>
                  <p className="text-xl font-bold text-slate-800">0</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">COD Collected</p>
                  <p className="text-xl font-bold text-slate-800">₹0</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Location Tracking Status */}
        <Card className="border-0 shadow-md">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  trackingActive ? "bg-emerald-100" : "bg-slate-100"
                }`}>
                  <Navigation className={`w-5 h-5 ${trackingActive ? "text-emerald-600" : "text-slate-400"}`} />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-700">Location Tracking</p>
                  <p className="text-xs text-slate-500">
                    {trackingActive 
                      ? `Active • Last update: ${lastLocationUpdate ? formatTime(lastLocationUpdate) : "Just now"}`
                      : "Inactive - Check in to start"
                    }
                  </p>
                </div>
              </div>
              <Badge className={trackingActive ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}>
                {trackingActive ? (
                  <>
                    <span className="w-2 h-2 rounded-full bg-emerald-500 mr-1.5 animate-pulse"></span>
                    Live
                  </>
                ) : (
                  "Off"
                )}
              </Badge>
            </div>
            {currentLocation && (
              <p className="text-xs text-slate-400 mt-2">
                Accuracy: {Math.round(currentLocation.accuracy || 0)}m
              </p>
            )}
          </CardContent>
        </Card>

        {/* Today's KM */}
        {attendance?.check_in_km && (
          <Card className="border-0 shadow-md">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Today's KM</p>
                  <p className="text-lg font-bold text-slate-800">
                    {attendance.check_out_km 
                      ? `${attendance.check_out_km - attendance.check_in_km} km`
                      : `Started at ${attendance.check_in_km} km`
                    }
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="pt-2">
          <p className="text-sm font-semibold text-slate-700 mb-3">Quick Actions</p>
          <div className="grid grid-cols-3 gap-3">
            <Button 
              variant="outline" 
              className="flex flex-col items-center py-4 h-auto"
              onClick={() => navigate("/rider/trips")}
            >
              <Package className="w-6 h-6 mb-1 text-blue-600" />
              <span className="text-xs">View Trips</span>
            </Button>
            <Button 
              variant="outline" 
              className="flex flex-col items-center py-4 h-auto"
              onClick={() => navigate("/rider/history")}
            >
              <Clock className="w-6 h-6 mb-1 text-purple-600" />
              <span className="text-xs">History</span>
            </Button>
            <Button 
              variant="outline" 
              className="flex flex-col items-center py-4 h-auto"
              onClick={() => navigate("/rider/cod")}
            >
              <Wallet className="w-6 h-6 mb-1 text-emerald-600" />
              <span className="text-xs">COD</span>
            </Button>
          </div>
        </div>
      </div>
    </RiderLayout>
  );
};

export default RiderDashboard;
